import _, {compact, isString, last} from 'lodash';

import {HandlerOf} from '../../../libs/shared/helpers/src/react/reactHelpers';
import {isHtml} from '../../../libs/shared/helpers/src/utilities/stringHelpers';
import {makeGetViewerMessages} from '../../../src/components/inboxes/side/sideFrameSelectors';
import {isTracker, teammateCanAccessInbox} from '../../../src/helpers/modelHelpers';
import {findCardForContactInNamespace} from '../../../src/helpers/models/contactHelpers';
import {makeFindConversationInboxIds} from '../../../src/helpers/models/conversationHelpers';
import {globalNamespace} from '../../../src/helpers/models/namespaceHelpers';
import {ReplyTypesEnum} from '../../../src/helpers/models/recipientHelpers';
import {getRecipientParser} from '../../../src/helpers/models/recipientParsers';
import {buildTagSearchUrl} from '../../../src/helpers/models/tagHelpers';
import {
  cardNoteToPluginJSON,
  channelToPluginJSON,
  conversationToPluginEventConversation,
  eventToPluginJSON,
  inboxToPluginJSON,
  messageToPluginJSON,
  recipientToPluginJSON,
  tagToPluginJSON,
  teammateToPluginJSON,
  topicToPluginJSON,
} from '../../../src/helpers/pluginHelpers';
import {ApiUploadedAttachments} from '../../../src/models/api/attachmentModel';
import {ApiCardNotes} from '../../../src/models/api/cardNoteModel';
import {ContactSourcesEnum} from '../../../src/models/api/contactModel';
import {ConversationTeamStatusesEnum} from '../../../src/models/api/conversationCommonTypes';
import {ConversationBaseModel, ConversationModel} from '../../../src/models/api/conversationModel';
import {MessageModel, MessageVisualStatusesEnum} from '../../../src/models/api/messageModel';
import {RecipientRolesEnum} from '../../../src/models/api/recipientModel';
import {ApiSearchTags} from '../../../src/models/api/searchTagModel';
import {ApiTag, TagBehaviorsEnum} from '../../../src/models/api/tagModel';
import {VisualRecipientModel} from '../../../src/models/api/visualRecipientModel';
import {tryParsePublicId} from '../../../src/models/applications/publicIdTypes';
import {
  SideFrameMessageAddTopicModel,
  SideFrameMessageAssignModel,
  SideFrameMessageComposeModel,
  SideFrameMessageCopyToClipboardModel,
  SideFrameMessageForwardModel,
  SideFrameMessageInsertHTMLModel,
  SideFrameMessageMoveToInboxModel,
  SideFrameMessageOpenUrlModel,
  SideFrameMessageReplyAllModel,
  SideFrameMessageReplyModel,
  SideFrameMessageSearchModel,
  SideFrameMessageTagModel,
  SideFrameMessageToggleTagModel,
  SideFrameMessageUntagModel,
  SideFrameMessageUpdateDraftModel,
} from '../../../src/models/frames/sideFrameMessageModel';
import {InboxesSearchLocationsEnum} from '../../../src/models/url/models/inboxesUrlModel';
import {findMessageComposer} from '../../../src/services/composers';
import {fetchApiAsync} from '../../../src/services/http/http';
import {createTopicAsync} from '../../../src/services/topics';
import {ReplyLocationsEnum} from '../../../src/services/tracking';
import {AppState} from '../../../src/store/appState';
import {ThunkAction, ThunkAsyncAction} from '../../../src/store/appThunk';
import {listLoadedRecipientsAction} from '../../../src/store/data/applications/applicationRecipientsActions';
import {fetchChannelByIdAsyncAction} from '../../../src/store/data/channels/channelsActions';
import {
  getChannels,
  getFindChannelIdFromChannelAddress,
  getUserParticipatingChannels,
} from '../../../src/store/data/channels/channelsSelectors';
import {getCompany} from '../../../src/store/data/company/companySelectors';
import {
  makeGetCard,
  makeGetChannel,
  makeGetContact,
  makeGetFallbackContact,
  makeGetRecipient,
} from '../../../src/store/data/contacts/recipientsSelectors';
import {
  assignToTeammateAction,
  canAssignToTeammateAction,
} from '../../../src/store/data/conversations/assigneeConversationsActions';
import {makeGetConversationInboxes} from '../../../src/store/data/conversations/conversationsSelectors';
import {
  canUserMoveToInboxAction,
  moveToInboxAction,
  MoveToInboxSourceTypesEnum,
} from '../../../src/store/data/conversations/moveConversationsActions';
import {
  addTagToConversationsAction,
  removeTagFromConversationsAction,
} from '../../../src/store/data/conversations/tagConversationsActions';
import {setConversationsTeamStatusAction} from '../../../src/store/data/conversations/teamStatusConversationsActions';
import {addTopicToConversationsAction} from '../../../src/store/data/conversations/topicConversationsActions';
import {getCards, getMessages} from '../../../src/store/data/dataSelectors';
import {fetchInboxByAliasAsyncAction} from '../../../src/store/data/inboxes/inboxesActions';
import {getInboxes, getUserTeamParticipatingInboxes} from '../../../src/store/data/inboxes/inboxesSelectors';
import {
  composeMessageAction,
  replyToMessageAction,
} from '../../../src/store/data/messages/draftMessagesActions';
import {getFindTeammate} from '../../../src/store/data/teammates/lazyTeammatesSelectors';
import {
  getFlatActiveTeammates,
  getTeammatesInCommonTeam,
  getUserId,
  getUserNamespace,
} from '../../../src/store/data/teammates/teammatesSelectors';
import {InboxesSearchParameters} from '../../../src/store/panels/inboxes/parameters/inboxesParameters';
import {ViewerPanelId} from '../../../src/store/panels/panelStateBase';
import {ViewerPanelPageState} from '../../../src/store/panels/viewer/viewerPanelState';
import {navigateToLinkAction} from '../../../src/store/stateless/navigationActions';
import {searchByParametersAction} from '../../../src/store/stateless/searchActions';
import {AppSelector} from '../../helpers/reselectHelpers';

export interface FrameViewerState {
  viewerPage: ViewerPanelPageState;
  conversation: ConversationModel;
  messages: ReadonlyArray<MessageModel>;
}

export interface SideFrameState {
  copyToClipboard: HandlerOf<string>;
  panelId: ViewerPanelId;
  getConversations: AppSelector<ReadonlyArray<ConversationBaseModel>>;
  viewerPage?: ViewerPanelPageState;
  conversation?: ConversationModel;
  messages?: ReadonlyArray<MessageModel>;
}

/*
 * Fetch data.
 */

export function fetchChannelsSideFrameAction(): ThunkAction<unknown> {
  return (_dispatch, getState) => {
    const state = getState();
    const inboxes = getInboxes(state);
    const userNamespace = getUserNamespace(state);

    return getUserParticipatingChannels(state).map((c) => {
      const inbox = inboxes[c.inboxId];
      const isPrivate = Boolean(inbox && inbox.namespace === userNamespace);

      return channelToPluginJSON(c, isPrivate);
    });
  };
}

export function fetchConversationSideFrameAction(sideFrameState: SideFrameState): ThunkAction<unknown> {
  return (_dispatch, getState) => {
    const state = getState();
    const {viewerPage, conversation, messages} = sideFrameState;
    if (!viewerPage || !conversation || !messages) {
      return {type: 'no_conversation'};
    }

    const {teammateId} = viewerPage.cellsContext;
    const pluginEventConversation = conversationToPluginEventConversation(state, conversation, teammateId);
    if (!pluginEventConversation) {
      return {type: 'no_conversation'};
    }

    const pluginEvent = {
      ...pluginEventConversation,
      messages,
    };

    return eventToPluginJSON(state, pluginEvent);
  };
}

export function fetchDraftSideFrameAction({conversation}: SideFrameState): ThunkAction<unknown> {
  return (_dispatch, getState) => {
    if (!conversation) {
      return null;
    }

    const {draftMessageUids} = conversation;
    if (draftMessageUids.length === 0) {
      return null;
    }

    const messages = getMessages(getState());
    const firstDraftUid = draftMessageUids[0];
    const firstDraft = messages[firstDraftUid];

    if (!firstDraft || firstDraft.visualStatus === MessageVisualStatusesEnum.PARTIAL) {
      return null;
    }

    return messageToPluginJSON(getState(), conversation, firstDraft);
  };
}

export function fetchInboxesSideFrameAction(): ThunkAction<unknown> {
  return (_dispatch, getState) => {
    const participatingInboxes = getUserTeamParticipatingInboxes(getState());
    return participatingInboxes.map(inboxToPluginJSON);
  };
}

export function fetchNotesSideFrameAction({
  viewerPage,
  conversation,
}: SideFrameState): ThunkAsyncAction<unknown> {
  return async (_dispatch, getState) => {
    if (!viewerPage || !conversation) {
      return [];
    }

    const state = getState();
    const getRecipient = makeGetRecipient(
      () => conversation,
      () => viewerPage.cellsContext.teammateId,
    );
    const getContact = makeGetContact(getRecipient);
    const contact = getContact(state, {});
    if (!contact) {
      return [];
    }

    const cards = getCards(state);
    const cardForConversation = findCardForContactInNamespace(contact, cards, conversation.namespace);
    if (!cardForConversation) {
      return [];
    }

    const notes = await fetchApiAsync(ApiCardNotes, `${cardForConversation.url}/card_notes`);
    const findTeammate = getFindTeammate(state);

    return notes.map((n) => cardNoteToPluginJSON(findTeammate, n));
  };
}

export function fetchTagsSideFrameAction({conversation}: SideFrameState): ThunkAsyncAction<unknown> {
  return async (_dispatch, getState) => {
    if (!conversation) {
      return [];
    }

    const state = getState();
    const company = getCompany(state);
    const tagSearchUrl = buildTagSearchUrl(company, '', {
      offset: 0,
      limit: 100,
      namespaces: [globalNamespace, conversation.namespace],
      inboxIds: conversation.inboxIds,
      behaviors: [TagBehaviorsEnum.CAN_APPLY_FROM_LIST],
    });

    const searchTags = await fetchApiAsync(ApiSearchTags, tagSearchUrl);
    return searchTags.tags.map((t) => tagToPluginJSON(t[0]));
  };
}

export function fetchTeamSideFrameAction(): ThunkAction<unknown> {
  return () => {
    throw new Error('This method is not supported.');
  };
}

export function fetchTeammatesSideFrameAction(sideFrameState: SideFrameState): ThunkAction<unknown> {
  return (_dispatch, getState) => {
    const state = getState();
    const {conversation} = sideFrameState;
    if (!conversation) {
      return [];
    }

    const getConversationInboxes = makeGetConversationInboxes(() => conversation);
    const conversationInboxes = getConversationInboxes(state, {});

    const commonTeammates = getTeammatesInCommonTeam(state);
    return _(commonTeammates)
      .filter((t) => {
        // Check the trackers.
        if (isTracker(conversation.trackers[t.id])) {
          return true;
        }

        // Check inboxes.
        if (conversationInboxes.some((i) => teammateCanAccessInbox(t, i))) {
          return true;
        }

        return false;
      })
      .map(teammateToPluginJSON)
      .value();
  };
}

export function fetchRecipientsSideFrameAction(sideFrameState: SideFrameState): ThunkAction<unknown> {
  return (dispatch, getState) => {
    const {conversation} = sideFrameState;
    if (!conversation) {
      return [];
    }
    const state = getState();
    const recipients = dispatch(listLoadedRecipientsAction(sideFrameState.panelId, conversation.id));
    return _(recipients)
      .map((r) => {
        const getContact = makeGetContact(() => r);
        const contact = getContact(state, {});
        if (!contact) {
          return {};
        }
        const fallbackContact = makeGetFallbackContact(getContact)(state, {});
        const channel = makeGetChannel(getContact)(state, {});
        const card = makeGetCard(() => conversation, getContact)(state, {});
        return {
          role: r.role,
          name: r.name,
          contact,
          fallbackContact,
          namespace: conversation.namespace,
          card,
          channel,
        };
      })
      .flatten()
      .map((vr: VisualRecipientModel) => recipientToPluginJSON(vr))
      .flatten()
      .value();
  };
}

/*
 * Conversation actions.
 */

export function addTopicSideFrameAction(
  {getConversations}: SideFrameState,
  sideFrameMessage: SideFrameMessageAddTopicModel,
): ThunkAsyncAction<unknown> {
  return async (dispatch, getState) => {
    // Finding conversations.
    const state = getState();
    const selectedConversations = getConversations(state);
    if (selectedConversations.length === 0) {
      throw new Error('There are no selected conversations.');
    }

    // Grabbing payload.
    const {topic} = sideFrameMessage;
    const {name, type} = topic;
    const link = topic.ext_link;

    // Creating topic.
    const company = getCompany(state);
    const newTopic = await createTopicAsync(company, name || '', link, type || 'web');

    // Add to conversations.
    const selectedConversationIds = selectedConversations.map((c) => c.id);
    dispatch(addTopicToConversationsAction(selectedConversationIds, newTopic.id));

    return topicToPluginJSON(newTopic);
  };
}

export function assignSideFrameAction(
  {getConversations}: SideFrameState,
  sideFrameMessage: SideFrameMessageAssignModel,
): ThunkAction {
  return (dispatch, getState) => {
    const {assignee} = sideFrameMessage;
    const state = getState();

    // Find the teammate to assign to.
    const teammates = getFlatActiveTeammates(state);
    const teammate = teammates.find((t) => t.alias === assignee);
    if (!teammate) {
      throw new Error(`Teammate with alias "${assignee}" could not be found.`);
    }

    // Find the conversations to assign.
    const conversations = getConversations(state);
    const conversationIds = conversations.map((c) => c.id);
    const findConversationInboxIds = makeFindConversationInboxIds(conversations);

    if (!dispatch(canAssignToTeammateAction(conversationIds, teammate, findConversationInboxIds))) {
      throw new Error(`Teammate "${assignee}" cannot be assigned to this conversation.`);
    }

    // Assign.
    dispatch(assignToTeammateAction(conversationIds, teammate.id));
  };
}

export function unassignSideFrameMethod({getConversations}: SideFrameState): ThunkAction {
  return (dispatch, getState) => {
    const selectedConversations = getConversations(getState());
    const selectedConversationIds = selectedConversations.map((c) => c.id);
    dispatch(assignToTeammateAction(selectedConversationIds, undefined));
  };
}

export function moveToSideFrameAction(
  {getConversations}: SideFrameState,
  sideFrameMessage: SideFrameMessageMoveToInboxModel,
): ThunkAsyncAction {
  return async (dispatch, getState) => {
    // Find the conversations to assign.
    const selectedConversations = getConversations(getState());
    if (selectedConversations.length === 0) {
      throw new Error('There are no selected conversations.');
    }

    // Find the inbox to move to.
    const inboxAlias = sideFrameMessage.inbox_alias;
    const inbox = await dispatch(fetchInboxByAliasAsyncAction(inboxAlias));
    if (!inbox) {
      throw new Error(`Inbox with alias "${inboxAlias}" could not be found.`);
    }

    const canMoveToInbox = dispatch(canUserMoveToInboxAction(inbox));
    if (!canMoveToInbox) {
      throw new Error(`Cannot move to inbox with alias "${inboxAlias}"`);
    }

    const selectedConversationIds = selectedConversations.map((c) => c.id);

    dispatch(
      moveToInboxAction(getUserId(getState()), selectedConversationIds, inbox.id, {
        type: MoveToInboxSourceTypesEnum.SIDE_FRAME,
        endpoint: sideFrameMessage.endpoint,
      }),
    );
  };
}

export function tagSideFrameAction(
  {conversation}: SideFrameState,
  sideFrameMessage: SideFrameMessageTagModel,
): ThunkAction {
  return async (dispatch, getState) => {
    if (!conversation) {
      return;
    }

    // Fetch the tag to apply.
    const tagAlias = sideFrameMessage.tag;
    const tag = await fetchTagByAliasAsync(getState(), conversation.namespace, tagAlias);

    // Apply it to the conversation.
    dispatch(addTagToConversationsAction([conversation.id], tag.id, [tag]));
  };
}

export function untagSideFrameMethod(
  {conversation}: SideFrameState,
  sideFrameMessage: SideFrameMessageUntagModel,
): ThunkAction {
  return async (dispatch, getState) => {
    if (!conversation) {
      return;
    }

    // Fetch the tag to remove.
    const tagAlias = sideFrameMessage.tag;
    const tag = await fetchTagByAliasAsync(getState(), conversation.namespace, tagAlias);

    // Remove from the conversation.
    dispatch(removeTagFromConversationsAction([conversation.id], tag.id));
  };
}

export function toggleTagSideFrameMethod(
  {conversation}: SideFrameState,
  sideFrameMessage: SideFrameMessageToggleTagModel,
): ThunkAction {
  return async (dispatch, getState) => {
    if (!conversation) {
      return;
    }

    // Fetch the tag to apply.
    const tagAlias = sideFrameMessage.tag;
    const tag = await fetchTagByAliasAsync(getState(), conversation.namespace, tagAlias);
    const hasTag = conversation.tagIds.includes(tag.id);

    // Add or remove the tag.
    if (hasTag) {
      dispatch(removeTagFromConversationsAction([conversation.id], tag.id));
    } else {
      dispatch(addTagToConversationsAction([conversation.id], tag.id, [tag]));
    }
  };
}

export function toggleArchivedSideFrameAction({getConversations}: SideFrameState): ThunkAction {
  return (dispatch, getState) => {
    // Find the conversations to assign.
    const conversations = getConversations(getState());
    if (conversations.length === 0) {
      throw new Error('There are no selected conversations.');
    }

    // Find out their current status
    const areAllClosed = conversations.every((c) => c.teamStatus === ConversationTeamStatusesEnum.ARCHIVED);

    // Find what status to apply.
    const newStatus = areAllClosed
      ? ConversationTeamStatusesEnum.OPEN
      : ConversationTeamStatusesEnum.ARCHIVED;

    // Apply that status.
    const conversationIds = conversations.map((c) => c.id);
    dispatch(setConversationsTeamStatusAction(conversationIds, newStatus));
  };
}

export function toggleTrashedSideFrameMethod({getConversations}: SideFrameState): ThunkAction {
  return (dispatch, getState) => {
    // Find the conversations to assign.
    const conversations = getConversations(getState());
    if (conversations.length === 0) {
      throw new Error('There are no selected conversations.');
    }

    // Find out their current status
    const areAllClosed = conversations.every((c) => c.teamStatus === ConversationTeamStatusesEnum.TRASHED);

    // Find what status to apply.
    const newStatus = areAllClosed ? ConversationTeamStatusesEnum.OPEN : ConversationTeamStatusesEnum.TRASHED;

    // Apply that status.
    const conversationIds = conversations.map((c) => c.id);
    return dispatch(setConversationsTeamStatusAction(conversationIds, newStatus));
  };
}

/*
 * Message actions.
 */

export function buildComposeSideFrameMethod(
  sanitizeHtml: (html: string) => string,
  textToHtml: (text: string) => string,
  htmlToText: (html: string) => string,
) {
  return function (
    sideFrameState: SideFrameState,
    sideFrameMessage: SideFrameMessageComposeModel,
  ): ThunkAction {
    return async (dispatch, getState) => {
      const state = getState();
      const {conversation} = sideFrameState;
      const {subject} = sideFrameMessage;
      const body = sideFrameMessage.body || '';

      // Find recipients + channel.
      const findChannelIdFromChannelAddress = getFindChannelIdFromChannelAddress(state);
      const channelId = findChannelIdFromChannelAddress(sideFrameMessage.from || '');
      const channels = getChannels(state);
      const channel = channelId ? channels[channelId] : undefined;
      const channelContactSource = channel?.contactSource || ContactSourcesEnum.EMAIL;
      const recipients = parseRecipientFromSideFrameMessage(sideFrameMessage, channelContactSource);
      // Fetch attachments if needed.
      const attachments = await maybeFetchAttachments(conversation, sideFrameMessage.attachment_uids);

      // Convert text to HTML and vice versa if necessary.
      const trustedHtml = isHtml(body) ? sanitizeHtml(body) : textToHtml(body);
      const text = isHtml(body) ? htmlToText(body) : body;

      dispatch(
        composeMessageAction({
          recipients,
          subject: subject || undefined,
          channelId,
          trustedHtml: trustedHtml || undefined,
          text: text || undefined,
          attachments,
        }),
      );
    };
  };
}

export function insertHtmlSideFrameAction(
  {conversation}: SideFrameState,
  sideFrameMessage: SideFrameMessageInsertHTMLModel,
) {
  tryInsertHtml(conversation, sideFrameMessage.html);
}

export function updateDraftSideFrameMethod(
  {conversation}: SideFrameState,
  sideFrameMessage: SideFrameMessageUpdateDraftModel,
): ThunkAction {
  return async (dispatch, getState) => {
    if (!conversation) {
      throw new Error('No conversation was found.');
    }

    const draftId = tryParsePublicId(sideFrameMessage.draft_id || '');
    if (!draftId) {
      throw new Error('Invalid draft ID');
    }

    const uids = conversation.draftMessageUids;
    if (!uids) {
      throw new Error('No draft found in the conversation');
    }

    const state = getState();
    const messages = getMessages(state);

    const draftUid = uids.find((uid) => {
      const message = messages[uid];
      return Boolean(message && message.id === draftId);
    });

    if (!draftUid) {
      throw new Error('No draft found for this id.');
    }

    const draft = messages[draftUid];
    if (!draft || draft.visualStatus !== MessageVisualStatusesEnum.DRAFT) {
      throw new Error('No draft found for this id.');
    }

    const channel = await dispatch(fetchChannelByIdAsyncAction(draft.fromChannelId));

    if (!channel) {
      throw new Error('The channel for the draft with this id could not be found.');
    }

    // We need the composer to call saveMessageAsyncAction from the composer
    // and not directly as we would need to know about the draft version
    // which we do not.
    const composer = findMessageComposer(draftUid);
    if (!composer) {
      throw new Error('No draft found for this id.');
    }

    // Removing previous attachments.
    const removedAttachmentCids = draft.attachments.map((a) => a.cid);

    // Fetching attachments given by the options.
    const attachmentUids = sideFrameMessage.attachment_uids as [string];
    const addedAttachments = await fetchAttachmentsInConversationAsync(attachmentUids, conversation);

    // Building recipients.
    const newRecipients = parseRecipientFromSideFrameMessage(
      sideFrameMessage.recipients || {},
      channel.contactSource,
    );

    const {saveMessage} = composer;
    if (!saveMessage) {
      throw new Error('Unable to call updateDraft for non email composers');
    }

    saveMessage({
      removedAttachmentCids,
      addedAttachments,
      newRecipients,
    });
  };
}

/*
 * Miscellaneous.
 */

export function copyToClipboardSideFrameAction(
  sideFrameState: SideFrameState,
  sideFrameMessage: SideFrameMessageCopyToClipboardModel,
) {
  const text = sideFrameMessage.payload.trim();
  if (!text) {
    return;
  }

  sideFrameState.copyToClipboard(text);
}

export function openUrlSideFrameAction(
  _sideFrameState: SideFrameState,
  sideFrameMessage: SideFrameMessageOpenUrlModel,
) {
  return navigateToLinkAction(sideFrameMessage.url);
}

export function searchSideFrameAction(
  _sideFrameState: SideFrameState,
  sideFrameMessage: SideFrameMessageSearchModel,
): ThunkAction {
  return (dispatch) => {
    const {query} = sideFrameMessage;
    if (!query) {
      return;
    }

    const searchParameters: InboxesSearchParameters = {
      location: InboxesSearchLocationsEnum.GLOBAL,
      query,
    };

    dispatch(searchByParametersAction(searchParameters));
  };
}

/*
 * Helpers.
 */

type SideFrameMessageReply =
  | SideFrameMessageReplyModel
  | SideFrameMessageReplyAllModel
  | SideFrameMessageForwardModel;

export function buildreplySideFrameMethod(
  sanitizeHtml: (html: string) => string,
  textToHtml: (text: string) => string,
  htmlToText: (html: string) => string,
) {
  return buildReplySideFrameMethodBase(sanitizeHtml, textToHtml, htmlToText, ReplyTypesEnum.SINGLE);
}

export function buildReplyAllSideFrameMethod(
  sanitizeHtml: (html: string) => string,
  textToHtml: (text: string) => string,
  htmlToText: (html: string) => string,
) {
  return buildReplySideFrameMethodBase(sanitizeHtml, textToHtml, htmlToText, ReplyTypesEnum.ALL);
}

export function buildForwardSideFrameMethod(
  sanitizeHtml: (html: string) => string,
  textToHtml: (text: string) => string,
  htmlToText: (html: string) => string,
) {
  return buildReplySideFrameMethodBase(sanitizeHtml, textToHtml, htmlToText, ReplyTypesEnum.FORWARD);
}

function buildReplySideFrameMethodBase(
  sanitizeHtml: (html: string) => string,
  textToHtml: (text: string) => string,
  htmlToText: (html: string) => string,
  replyType: ReplyTypesEnum,
) {
  return function (
    sideFrameState: SideFrameState,
    sideFrameMessage: SideFrameMessageReply,
  ): ThunkAsyncAction<unknown> {
    return async (dispatch, getState) => {
      // Make sure we're in the correct state.
      const {panelId, viewerPage, conversation} = sideFrameState;
      if (!viewerPage || !conversation) {
        return;
      }

      const state = getState();

      // Find the message to reply to.
      const messages = makeGetViewerMessages(() => viewerPage)(state, {});
      const lastMessage = last(messages);
      if (!lastMessage) {
        return;
      }

      const {subject} = sideFrameMessage;
      const body = sideFrameMessage.body || '';
      const attachmentUids = sideFrameMessage.attachment_uids || [];

      // Fetch attachments if needed.
      const attachments = await fetchAttachmentsInConversationAsync(attachmentUids, conversation);

      // Parse optional recipients.
      const getRecipient = makeGetRecipient(
        () => conversation,
        () => viewerPage.cellsContext.teammateId,
      );
      const getContact = makeGetContact(getRecipient);
      const contact = getContact(state, {});
      const contactSource = contact ? contact.source : ContactSourcesEnum.EMAIL;
      const recipients = parseRecipientFromSideFrameMessage(sideFrameMessage, contactSource);

      // Convert text to HTML and vice versa if necessary.
      const trustedHtml = isHtml(body) ? sanitizeHtml(body) : textToHtml(body);
      const text = isHtml(body) ? htmlToText(body) : body;

      if (replyType !== ReplyTypesEnum.FORWARD && tryInsertHtml(conversation, trustedHtml)) {
        return;
      }

      // Dispatch the reply action.
      dispatch(
        replyToMessageAction({
          panelId,
          replyType,
          replyToUid: lastMessage.uid,
          conversationId: conversation.id,
          location: ReplyLocationsEnum.PLUGIN_API,
          overrides: {
            attachments,
            trustedHtml,
            text,
            subject: isString(subject) ? subject : undefined,
            recipients,
          },
        }),
      );
    };
  };
}

function tryInsertHtml(conversation: ConversationModel | undefined, html: string) {
  if (!conversation) {
    return false;
  }

  const uids = conversation && [
    ...conversation.draftMessageUids,
    ...(conversation.unsavedDraftMessageUids || []),
  ];
  if (!uids || uids.length === 0) {
    return false;
  }

  const composer = _(uids)
    .map((uid) => findMessageComposer(uid))
    .find();

  if (!composer) {
    return false;
  }

  // Insert HTML.
  composer.insertHtml(html);
  return true;
}

interface RecipientsObject {
  to?: ReadonlyArray<string> | null;
  bcc?: ReadonlyArray<string> | null;
  cc?: ReadonlyArray<string> | null;
}

function parseRecipientFromSideFrameMessage(recipients: RecipientsObject, source: ContactSourcesEnum) {
  const parser = getRecipientParser(source);
  if (!parser) {
    return undefined;
  }

  if (!recipients.to && !recipients.cc && !recipients.bcc) {
    return undefined;
  }

  return compact([
    ...(recipients.to || []).map((r) => parser(RecipientRolesEnum.TO, r)),
    ...(recipients.cc || []).map((r) => parser(RecipientRolesEnum.CC, r)),
    ...(recipients.bcc || []).map((r) => parser(RecipientRolesEnum.BCC, r)),
  ]);
}

async function fetchAttachmentsInConversationAsync(
  uids: ReadonlyArray<string>,
  conversation: ConversationModel,
) {
  // They didn't specify any attachments. let's not override anything.
  if (!uids) {
    return [];
  }

  // They do not want any attachments. No need to fetch attachments from the server.
  if (uids.length === 0) {
    return [];
  }

  // Requesting attachments and matching with user payload.
  const attachments = await fetchApiAsync(ApiUploadedAttachments, `${conversation.url}/attachments`);
  return compact(uids.map((uid) => attachments.find((a) => a.uid === uid)));
}

function fetchTagByAliasAsync(state: AppState, namespace: string, alias: string) {
  if (!alias) {
    throw new Error('A valid tag alias is required.');
  }

  const company = getCompany(state);
  const url = `${company.url}/namespaces/${namespace}/tags/${alias}`;

  return fetchApiAsync(ApiTag, url);
}

async function maybeFetchAttachments(
  conversation?: ConversationModel,
  attachmentUids?: ReadonlyArray<string> | null,
) {
  if (!attachmentUids || attachmentUids.length < 1 || !conversation) {
    return [];
  }

  return fetchAttachmentsInConversationAsync(attachmentUids, conversation);
}
